import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Trash2, Users } from 'lucide-react';
import { getSupabaseClient, hasSupabaseConfig } from '../lib/supabaseClient';
import { formatSupabaseError } from '../lib/supabaseErrors';
import { useAuth } from '../auth/AuthContext';
import { useModuleAccess } from '../auth/useModuleAccess';
import { normalizeModules, normalizeRole } from '../auth/moduleAccess';
import {
  createRosterMember,
  listOrgRoster,
  updateRosterMember,
} from '../db/sync/rosterSync';
import SignedInUserLine from '../components/common/SignedInUserLine';

export default function RosterPage() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { switchOrganization, ready, memberships } = useAuth();
  const { canAdmin } = useModuleAccess();
  const [members, setMembers] = useState([]);
  const [roster, setRoster] = useState([]);
  const [rosterFirst, setRosterFirst] = useState('');
  const [rosterLast, setRosterLast] = useState('');
  const [rosterEmail, setRosterEmail] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const membershipForOrg = memberships.find((m) => m.organization_id === orgId);

  useEffect(() => {
    if (!ready) return;
    if (!hasSupabaseConfig()) {
      navigate('/', { replace: true });
      return;
    }
    if (orgId) void switchOrganization(orgId);
  }, [ready, orgId, switchOrganization, navigate]);

  useEffect(() => {
    if (ready && orgId && membershipForOrg && !canAdmin) {
      navigate(`/orgs/${orgId}`, { replace: true });
    }
  }, [ready, orgId, canAdmin, membershipForOrg, navigate]);

  const load = useCallback(async () => {
    const supabase = getSupabaseClient();
    if (!supabase || !orgId) return;
    setError(null);
    try {
      const { data: list, error: listErr } = await supabase.rpc('list_org_memberships', {
        p_organization_id: orgId,
      });
      if (listErr) throw listErr;
      setMembers(
        (list ?? []).map((row) => {
          const role = normalizeRole(row.role);
          return {
            ...row,
            role,
            modules: normalizeModules(role, row.modules),
          };
        })
      );

      const rosterRows = await listOrgRoster(orgId, { activeOnly: false });
      setRoster(rosterRows);
    } catch (err) {
      console.error('[Roster] load failed', err);
      setError(formatSupabaseError(err, 'Failed to load roster'));
      setRoster([]);
    }
  }, [orgId]);

  useEffect(() => {
    if (canAdmin && orgId && membershipForOrg) void load();
  }, [canAdmin, orgId, membershipForOrg, load]);

  const addRosterPerson = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const match = members.find(
        (m) =>
          rosterEmail.trim() &&
          String(m.email || '').toLowerCase() === rosterEmail.trim().toLowerCase()
      );
      await createRosterMember(orgId, {
        firstName: rosterFirst,
        lastName: rosterLast,
        email: rosterEmail.trim() || null,
        userId: match?.user_id || null,
      });
      setRosterFirst('');
      setRosterLast('');
      setRosterEmail('');
      await load();
    } catch (err) {
      setError(formatSupabaseError(err, 'Could not add roster member'));
    } finally {
      setBusy(false);
    }
  };

  const deactivateRosterPerson = async (row) => {
    if (!window.confirm(`Deactivate ${row.displayName} on the roster?`)) return;
    setBusy(true);
    setError(null);
    try {
      await updateRosterMember(row.id, { isActive: false });
      await load();
    } catch (err) {
      setError(formatSupabaseError(err, 'Could not update roster member'));
    } finally {
      setBusy(false);
    }
  };

  const linkRosterToLogin = async (row, userId) => {
    setBusy(true);
    setError(null);
    try {
      await updateRosterMember(row.id, { userId });
      await load();
    } catch (err) {
      setError(formatSupabaseError(err, 'Could not link roster member'));
    } finally {
      setBusy(false);
    }
  };

  const orgName = membershipForOrg?.organization_name || 'Organization';

  return (
    <div className="min-h-screen bg-pp-paper text-pp-ink">
      <header className="border-b border-pp-line/80 bg-pp-surface">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <div>
            <Link
              to={`/orgs/${orgId}`}
              className="inline-flex items-center gap-1 text-xs font-medium text-pp-muted hover:text-pp-ink"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to {orgName}
            </Link>
            <h1 className="mt-0.5 flex items-center gap-2 text-xl font-semibold tracking-tight">
              <Users className="h-5 w-5 text-pp-accent" aria-hidden />
              Member roster
            </h1>
            <p className="text-sm text-pp-muted">
              Names for roll call and committee labels — logins optional
            </p>
            <SignedInUserLine className="mt-1" />
          </div>
          <Link
            to={`/orgs/${orgId}/admin`}
            className="rounded-xl border border-pp-line px-3 py-2 text-sm font-medium text-pp-muted hover:bg-pp-paper"
          >
            Administration
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl space-y-6 px-4 py-8 sm:px-6">
        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        <section className="rounded-2xl border border-pp-line bg-pp-surface p-6 shadow-pp">
          <h2 className="text-base font-semibold">Add person</h2>
          <p className="mt-1 text-sm text-pp-muted">
            Matching an email to an org login links them automatically when you add.
          </p>
          <form className="mt-4 grid gap-3 sm:grid-cols-2" onSubmit={addRosterPerson}>
            <div>
              <label className="text-xs font-medium text-pp-muted">First name</label>
              <input
                type="text"
                required
                value={rosterFirst}
                onChange={(e) => setRosterFirst(e.target.value)}
                className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-pp-muted">Last name</label>
              <input
                type="text"
                required
                value={rosterLast}
                onChange={(e) => setRosterLast(e.target.value)}
                className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-pp-muted">Email (optional)</label>
              <input
                type="email"
                value={rosterEmail}
                onChange={(e) => setRosterEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={busy}
                className="rounded-xl bg-pp-accent px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                {busy ? 'Saving…' : 'Add to roster'}
              </button>
            </div>
          </form>
        </section>

        <section className="rounded-2xl border border-pp-line bg-pp-surface p-6 shadow-pp">
          <h2 className="text-base font-semibold">Roster</h2>
          <ul className="mt-4 divide-y divide-pp-line">
            {roster.length === 0 ? (
              <li className="py-3 text-sm text-pp-muted">No roster members yet.</li>
            ) : (
              roster.map((row) => (
                <li key={row.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
                  <div>
                    <p className={`font-medium ${row.isActive ? '' : 'text-pp-muted line-through'}`}>
                      {row.displayName}
                    </p>
                    <p className="text-xs text-pp-muted">
                      {row.email || 'No email'}
                      {row.userId ? ' · Has login' : ' · No login'}
                      {!row.isActive ? ' · Inactive' : ''}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {!row.userId && members.length > 0 && (
                      <select
                        className="rounded-lg border border-pp-line bg-white px-2 py-1 text-xs"
                        defaultValue=""
                        onChange={(e) => {
                          if (e.target.value) void linkRosterToLogin(row, e.target.value);
                        }}
                      >
                        <option value="">Link login…</option>
                        {members
                          .filter((m) => m.is_active !== false)
                          .map((m) => (
                            <option key={m.user_id} value={m.user_id}>
                              {m.display_name || m.email}
                            </option>
                          ))}
                      </select>
                    )}
                    {row.isActive && (
                      <button
                        type="button"
                        onClick={() => void deactivateRosterPerson(row)}
                        className="inline-flex items-center gap-1 rounded-lg border border-pp-line px-2 py-1 text-xs font-medium text-pp-muted hover:border-rose-300 hover:text-rose-700"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Deactivate
                      </button>
                    )}
                  </div>
                </li>
              ))
            )}
          </ul>
        </section>
      </main>
    </div>
  );
}
